import Share from '@pelckmans/business-components/icons/Share';
import DropdownMenu from '@pelckmans/elementis/components/dropdown-menu';
import List from '@pelckmans/elementis/components/list';
import { useToast } from '@pelckmans/elementis/components/toast';
import DisplayOn from '@pelckmans/elementis/icons/display-on';
import Edit from '@pelckmans/elementis/icons/edit';
import Trash from '@pelckmans/elementis/icons/trash';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { ValuesOfObject } from '../../../@types/utils';
import type { Module } from '../../../api/module';
import type { Node, NodeWithoutDescription } from '../../../api/table-of-content';
import type { UserMaterial } from '../../../api/user-material';
import { isScorableLti, validateAndGetLinkFromUserMaterial } from '../../../api/user-material';
import type { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../constants/analyticsEvents';
import { ANALYTICS_EVENT_TYPES } from '../../../constants/analyticsEvents';
import { TOAST_ID } from '../../../constants/ToastIds';
import IdentityContext from '../../../contexts/IdentityContext';
import ModalContext from '../../../contexts/ModalContext';
import useAnalytics from '../../../hooks/useAnalytics';
import ActionMenuButton from '../../../shared-components/ActionMenuButton';
import ConfirmationModal from '../../../shared-components/ConfirmationModal';
import ShareModal from '../../../shared-components/share-modal/ShareModal';
import SelectionContext from '../toc/_contexts/SelectionContext';
import MaterialPills from '../toc/components/MaterialPills';
import PinnedMaterialButton from '../toc/components/pinned-material/PinnedMaterialButton';
import ScorableProgress from '../toc/components/ScorableProgress';
import EditMaterialModal from '../toc/components/teacher/EditMaterialModal';
import useUserMaterial from '../toc/hooks/user-material/useUserMaterial';
import { MaterialIcon } from './MaterialIcon';

interface Props {
  nodes: Node[] | NodeWithoutDescription[];
  module: Module;
  userMaterial: UserMaterial;
  canBePinned?: boolean;
  subLocationId: ValuesOfObject<typeof ANALYTICS_EVENT_SUBLOCATIONS>;
  refetch: () => void;
  isShared?: boolean;
}

function UserMaterialItem({
  nodes,
  module,
  userMaterial: material,
  canBePinned,
  subLocationId,
  refetch,
  isShared = false,
}: Props) {
  const { identity } = useContext(IdentityContext);

  const { t } = useTranslation();
  const { capture } = useAnalytics();
  const { showSelection, selectedUserMaterials, addUserMaterials, removeUserMaterials } = useContext(SelectionContext);
  const { openMaterial, removeUserMaterial } = useUserMaterial();
  const { showModal } = useContext(ModalContext);
  const { addToast } = useToast();

  const link = validateAndGetLinkFromUserMaterial(material);

  const pillGroupProps = {
    'aria-label': t('PAGES.TOC_LEVEL.MATERIAL_PILLS.A11Y_LABEL'),
    children: <MaterialPills isShared={isShared} isAssignment={!!material.assignment} />,
    role: 'group',
  };

  const handleOpenMaterial = async (material: UserMaterial) => {
    const modal = await openMaterial(
      material,
      nodes,
      material.userMaxScore
        ? async () => {
            refetch();
          }
        : undefined,
      subLocationId,
    );

    if (modal) {
      showModal(modal);
    }
  };

  const onMaterialEdited = () => {
    addToast({
      id: TOAST_ID.EDIT_MATERIAL_SUCCESS,
      variant: 'success',
      title: t('PAGES.TOC_LEVEL.TOASTS.EDIT_MATERIAL.SUCCESS.TITLE'),

      children: t('PAGES.TOC_LEVEL.TOASTS.EDIT_MATERIAL.SUCCESS.MESSAGE'),
    });

    return refetch();
  };

  const showEditMaterialModal = (material: UserMaterial) => {
    return showModal(
      <EditMaterialModal
        id="edit-material-modal"
        nodeId={material.node}
        module={module}
        material={material}
        onSubmitted={onMaterialEdited}
      />,
    );
  };

  const showShareModal = (userMaterial: UserMaterial) => {
    return showModal(
      <ShareModal
        id="share-modal"
        moduleId={module.id}
        nodeId={material.node}
        userMaterials={[userMaterial]}
        onSubmitted={refetch}
      />,
    );
  };

  function captureUserMaterialDeletedEvent(userMaterialId: string) {
    capture({
      objectId: userMaterialId,
      eventType: ANALYTICS_EVENT_TYPES.USER_MATERIAL_DELETED,
      subLocationId: subLocationId,
    });
  }

  const handleDelete = async (material: UserMaterial) => {
    await removeUserMaterial(material);
    refetch();
    captureUserMaterialDeletedEvent(material.id);
  };

  const showDeleteModal = (material: UserMaterial) => {
    const BASE = 'PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.DELETE_USER_MATERIAL.CONFIRMATION_MODAL';
    return showModal(
      <ConfirmationModal
        id={material.id.toString()}
        headerTitle={t(`${BASE}.HEADER_TITLE`, { name: material.name })}
        title={t(`${BASE}.TITLE`)}
        message={t(`${BASE}.BODY`)}
        confirmationText={t(`${BASE}.CONFIRMATION_TEXT`)}
        onConfirm={() => handleDelete(material)}
      />,
    );
  };

  function ActionsMenu() {
    if (!identity.isTeacherAlike) return null;

    return (
      <ActionMenuButton
        menuProps={{
          ariaLabel: t('PAGES.TOC_LEVEL.ACTION_MENU.A11Y_LABEL'),
        }}
        buttonProps={{
          ariaLabel: t('PAGES.TOC_LEVEL.ACTION_MENU.BUTTON.A11Y_LABEL'),
        }}
      >
        {handleToggle => (
          <>
            {link ? (
              <DropdownMenu.Item
                text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.OPEN_MATERIAL.LABEL')}
                textIcon={<DisplayOn />}
                as="link"
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                role="menuitem"
                onClick={() => {
                  handleOpenMaterial(material);
                  handleToggle();
                }}
              />
            ) : (
              <DropdownMenu.Item
                text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.OPEN_MATERIAL.LABEL')}
                textIcon={<DisplayOn />}
                as="button"
                role="menuitem"
                onClick={() => {
                  handleOpenMaterial(material);
                  handleToggle();
                }}
              />
            )}
            <DropdownMenu.Item
              text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.EDIT_MATERIAL.LABEL')}
              textIcon={<Edit />}
              as="button"
              role="menuitem"
              showSeparator
              onClick={() => {
                showEditMaterialModal(material);
                handleToggle();
              }}
            />
            <DropdownMenu.Item
              text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.SHARE_MATERIAL.LABEL')}
              textIcon={<Share />}
              as="button"
              role="menuitem"
              onClick={() => {
                showShareModal(material);
                handleToggle();
              }}
            />
            <DropdownMenu.Item
              prominence="danger"
              text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.DELETE_USER_MATERIAL.LABEL')}
              textIcon={<Trash />}
              as="button"
              role="menuitem"
              showSeparator
              onClick={() => {
                showDeleteModal(material);
                handleToggle();
              }}
            />
          </>
        )}
      </ActionMenuButton>
    );
  }

  const actionArea = (
    <>
      {canBePinned && <PinnedMaterialButton type="user-material" materialId={material.id} />}
      {!showSelection && <ActionsMenu />}
    </>
  );

  if (showSelection) {
    const isSelected = selectedUserMaterials.includes(material);
    return (
      <List.Item
        key={material.id}
        title={material.name}
        contentArea={isScorableLti(material) && <ScorableProgress score={material.score} />}
        actionArea={actionArea}
        pillGroupProps={pillGroupProps}
        onClick={() => (isSelected ? removeUserMaterials([material]) : addUserMaterials([material]))}
        as="checkbox"
        checked={isSelected}
      />
    );
  }

  if (link) {
    return (
      <List.Item
        key={material.id}
        title={material.name}
        icon={<MaterialIcon materialType="user-material" />}
        actionArea={actionArea}
        pillGroupProps={pillGroupProps}
        onClick={() => handleOpenMaterial(material)}
        as="link"
        target="_blank"
        rel="noopener noreferrer"
        href={link}
      />
    );
  }

  return (
    <List.Item
      key={material.id}
      title={material.name}
      icon={<MaterialIcon materialType="user-material" />}
      contentArea={isScorableLti(material) && <ScorableProgress score={material.score} />}
      actionArea={actionArea}
      pillGroupProps={pillGroupProps}
      onClick={() => handleOpenMaterial(material)}
      as="button"
    />
  );
}

export default UserMaterialItem;
